// Backend · Alerts: the monitoring-mode radio group (Off · Always · Campaign
// running). The Rules tab renders one per row; both rule drawers render one.

import { useId, useRef, type KeyboardEvent } from 'react';
import type { ActivationModeId, ActivationOption } from '@/lib/alert-activation';

export interface ActivationSelectorProps {
  value: ActivationModeId;
  options: readonly ActivationOption[];
  onChange: (mode: ActivationModeId) => void;
  label: string;
  disabled?: boolean;
  busy?: boolean;
  /**
   * The `id` of the visible line that says why an unavailable option cannot
   * be chosen; the unavailable options are described by it.
   */
  unavailableDescriptionId?: string;
}

export function ActivationSelector({
  value,
  options,
  onChange,
  label,
  disabled = false,
  busy = false,
  unavailableDescriptionId,
}: ActivationSelectorProps) {
  const groupId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(0, options.findIndex((option) => option.id === value));
  const choose = (index: number) => {
    const option = options[index];
    if (disabled || busy || !option || option.unavailable) return;
    onChange(option.id);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (disabled || busy) return;
    let next: number | null = null;
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step) {
      for (let offset = 1; offset <= options.length; offset += 1) {
        const candidate = (index + step * offset + options.length) % options.length;
        if (!options[candidate]?.unavailable) {
          next = candidate;
          break;
        }
      }
    }
    if (event.key === 'Home') next = options.findIndex((option) => !option.unavailable);
    if (event.key === 'End') {
      for (let candidate = options.length - 1; candidate >= 0; candidate -= 1) {
        if (!options[candidate]?.unavailable) {
          next = candidate;
          break;
        }
      }
    }
    if (next == null) return;
    event.preventDefault();
    choose(next);
    refs.current[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-busy={busy || undefined}
      aria-disabled={disabled || undefined}
      style={{
        display: 'inline-flex', alignItems: 'stretch', overflow: 'hidden',
        border: '1px solid var(--line)', borderRadius: 'var(--r)', background: 'var(--panel)',
        opacity: disabled ? 0.65 : 1,
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {options.map((option, index) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            id={`${groupId}-${option.id}`}
            ref={(node) => { refs.current[index] = node; }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={option.unavailable || undefined}
            aria-describedby={option.unavailable ? unavailableDescriptionId : undefined}
            title={option.unavailable ? option.unavailableReason : undefined}
            // Not `disabled` while busy: disabling the focused radio would
            // dump keyboard focus onto <body>. `choose`/`onKeyDown` already
            // ignore input during a save, and the group is aria-busy.
            disabled={disabled}
            tabIndex={index === selectedIndex ? 0 : -1}
            onClick={() => choose(index)}
            onKeyDown={(event) => onKeyDown(event, index)}
            style={{
              minHeight: 28,
              padding: '4px 8px',
              border: 0,
              borderLeft: index ? '1px solid var(--line)' : 0,
              background: selected ? 'var(--ink)' : 'transparent',
              // An unavailable option is dimmed and struck out, never in the
              // warning colour: --ink-3 keeps it >= 4.5:1 on --panel in both
              // themes (alert-mute.contrast.test.ts), which opacity would not.
              color: selected ? 'var(--bg)' : option.unavailable ? 'var(--ink-3)' : 'var(--ink-2)',
              textDecoration: option.unavailable ? 'line-through' : undefined,
              cursor: disabled || busy || option.unavailable ? 'default' : 'pointer',
              fontFamily: 'var(--font-ui)',
              fontSize: 10.5,
              fontWeight: selected ? 650 : 500,
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
